import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Divider,
    Chip,
    IconButton,
    useTheme,
    alpha,
    Collapse,
    Badge,
    Tooltip,
    Paper
} from '@mui/material';
import {
    ShoppingCart as ShoppingCartIcon,
    Receipt as ReceiptIcon,
    BarChart as BarChartIcon,
    Quiz as QuizIcon,
    QuestionAnswer as QuestionAnswerIcon,
    People as PeopleIcon,
    Email as EmailIcon,
    LocalOffer as LocalOfferIcon,
    PointOfSale as PointOfSaleIcon,
    History as HistoryIcon,
    Dashboard as DashboardIcon,
    Settings as SettingsIcon,
    Notifications as NotificationsIcon,
    AccountCircle as AccountCircleIcon,
    Logout as LogoutIcon,
    ChevronLeft as ChevronLeftIcon,
    Menu as MenuIcon,
    ExpandLess,
    ExpandMore,
    TrendingUp as TrendingUpIcon,
    Store as StoreIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const AdminLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [drawerCollapsed, setDrawerCollapsed] = useState(false);
    const [salesExpanded, setSalesExpanded] = useState(true);
    const [usersExpanded, setUsersExpanded] = useState(true);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleDrawerCollapse = () => {
        setDrawerCollapsed(!drawerCollapsed);
    };

    const isActiveRoute = (path) => {
        return location.pathname === path;
    };

    const navigationGroups = [
        {
            title: 'Dashboard',
            items: [
                { text: 'Overview', icon: <DashboardIcon />, link: '/admin/dashboard', badge: null }
            ]
        },
        {
            title: 'Sales & Commerce',
            expanded: salesExpanded,
            setExpanded: setSalesExpanded,
            items: [
                { text: 'Products', icon: <StoreIcon />, link: '/admin/products', badge: null },
                { text: 'Orders', icon: <ReceiptIcon />, link: '/admin/orders', badge: null },
                { text: 'In-Office Purchase', icon: <PointOfSaleIcon />, link: '/admin/in-office-purchase', badge: null },
                { text: 'Coupons', icon: <LocalOfferIcon />, link: '/admin/coupons', badge: null },
                { text: 'Revenue', icon: <TrendingUpIcon />, link: '/admin/revenue', badge: null }
            ]
        },
        {
            title: 'Learning Management',
            expanded: usersExpanded,
            setExpanded: setUsersExpanded,
            items: [
                { text: 'Subscriptions', icon: <QuizIcon />, link: '/admin/subscriptions', badge: null },
                { text: 'Test Questions', icon: <QuestionAnswerIcon />, link: '/admin/test-questions', badge: null },
                { text: 'Users', icon: <PeopleIcon />, link: '/admin/users', badge: null },
                { text: 'User History', icon: <HistoryIcon />, link: '/admin/user-history', badge: null },
                { text: 'Analytics', icon: <AssessmentIcon />, link: '/admin/analytics', badge: null }
            ]
        },
        {
            title: 'Communication',
            items: [
                { text: 'Emails', icon: <EmailIcon />, link: '/admin/emails', badge: null }
            ]
        }
    ];

    const currentDrawerWidth = drawerCollapsed ? collapsedDrawerWidth : drawerWidth;

    const DrawerContent = () => (
        <Box
            sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #ff5e17 0%, #ffffff 100%)',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,94,23,0.1) 100%)',
                    zIndex: 1
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: drawerCollapsed ? 1 : 3,
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    zIndex: 2,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    {!drawerCollapsed && (
                        <Box display="flex" alignItems="center" gap={2}>
                            <Avatar
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    color: '#ff5e17',
                                    width: 48,
                                    height: 48,
                                    border: '2px solid rgba(255,255,255,0.3)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    fontWeight: 800,
                                    fontSize: '1.1rem'
                                }}
                            >
                                CT
                            </Avatar>
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#1a1a1a',
                                        fontWeight: 800,
                                        fontSize: '1.2rem',
                                        lineHeight: 1.2,
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    Contractor Test LA
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: alpha('#1a1a1a', 0.6),
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    ADMIN PORTAL
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {drawerCollapsed && (
                        <Avatar
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.9)',
                                color: '#ff5e17',
                                width: 36,
                                height: 36,
                                mx: 'auto',
                                border: '2px solid rgba(255,255,255,0.3)',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                fontWeight: 800
                            }}
                        >
                            CT
                        </Avatar>
                    )}
                </Box>
            </Box>

            {/* Navigation */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    position: 'relative',
                    zIndex: 2,
                    '&::-webkit-scrollbar': {
                        width: '4px'
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: alpha('#000', 0.15),
                        borderRadius: '2px'
                    }
                }}
            >
                <List sx={{ pt: 2 }}>
                    {navigationGroups.map((group, groupIndex) => (
                        <React.Fragment key={group.title}>
                            {!drawerCollapsed && group.title && (
                                <Box sx={{ px: 3, py: 1.5, mt: groupIndex > 0 ? 1 : 0 }}>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            color: alpha('#1a1a1a', 0.5),
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            letterSpacing: '1.5px'
                                        }}
                                    >
                                        {group.title}
                                    </Typography>
                                </Box>
                            )}

                            {group.setExpanded && !drawerCollapsed && (
                                <ListItem
                                    button
                                    onClick={() => group.setExpanded(!group.expanded)}
                                    sx={{
                                        mx: 2,
                                        borderRadius: 3,
                                        mb: 0.5,
                                        background: 'rgba(255,255,255,0.08)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            color: '#1a1a1a',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                        },
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            <Typography
                                                sx={{
                                                    color: '#1a1a1a',
                                                    fontWeight: 600,
                                                    fontSize: '0.85rem',
                                                    letterSpacing: '0.3px'
                                                }}
                                            >
                                                {group.title}
                                            </Typography>
                                        }
                                    />
                                    {group.expanded ?
                                        <ExpandLess sx={{ color: '#1a1a1a', fontSize: '1.2rem' }} /> :
                                        <ExpandMore sx={{ color: '#1a1a1a', fontSize: '1.2rem' }} />
                                    }
                                </ListItem>
                            )}

                            <Collapse in={drawerCollapsed || !group.setExpanded || group.expanded} timeout="auto" unmountOnExit>
                                {group.items.map((item) => {
                                    const isActive = isActiveRoute(item.link);
                                    return (
                                        <Tooltip
                                            key={item.text}
                                            title={drawerCollapsed ? item.text : ''}
                                            placement="right"
                                        >
                                            <ListItem
                                                component={Link}
                                                to={item.link}
                                                sx={{
                                                    mx: drawerCollapsed ? 1 : 2,
                                                    borderRadius: 3,
                                                    mb: 0.5,
                                                    backgroundColor: isActive ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.05)',
                                                    border: isActive ? '1px solid rgba(255,255,255,0.4)' : '1px solid rgba(255,255,255,0.1)',
                                                    backdropFilter: 'blur(10px)',
                                                    boxShadow: isActive ? '0 4px 20px rgba(0,0,0,0.15)' : '0 2px 10px rgba(0,0,0,0.05)',
                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    transform: isActive ? 'translateX(6px) translateY(-2px)' : 'translateX(0)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255,255,255,0.95)',
                                                        transform: 'translateX(6px) translateY(-2px)',
                                                        boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
                                                        border: '1px solid rgba(255,255,255,0.6)',
                                                        '& .MuiListItemIcon-root': {
                                                            transform: 'scale(1.1)',
                                                            color: '#ff5e17 !important'
                                                        },
                                                        '& .MuiTypography-root': {
                                                            color: '#1a1a1a !important',
                                                            fontWeight: '600 !important'
                                                        }
                                                    }
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        color: isActive ? '#ff5e17' : alpha('#1a1a1a', 0.6),
                                                        minWidth: drawerCollapsed ? 'auto' : 40,
                                                        justifyContent: 'center',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    {item.badge ? (
                                                        <Badge
                                                            badgeContent={item.badge}
                                                            sx={{
                                                                '& .MuiBadge-badge': {
                                                                    backgroundColor: '#ff5722',
                                                                    color: '#fff',
                                                                    fontSize: '0.7rem',
                                                                    minWidth: 18,
                                                                    height: 18
                                                                }
                                                            }}
                                                        >
                                                            {item.icon}
                                                        </Badge>
                                                    ) : (
                                                        item.icon
                                                    )}
                                                </ListItemIcon>
                                                {!drawerCollapsed && (
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                sx={{
                                                                    color: isActive ? '#1a1a1a' : alpha('#1a1a1a', 0.7),
                                                                    fontWeight: isActive ? 600 : 500,
                                                                    fontSize: '0.9rem',
                                                                    letterSpacing: '0.3px'
                                                                }}
                                                            >
                                                                {item.text}
                                                            </Typography>
                                                        }
                                                    />
                                                )}
                                            </ListItem>
                                        </Tooltip>
                                    );
                                })}
                            </Collapse>

                            {groupIndex < navigationGroups.length - 1 && !drawerCollapsed && (
                                <Divider
                                    sx={{
                                        my: 3,
                                        mx: 3,
                                        borderColor: alpha('#1a1a1a', 0.08),
                                        '&::before, &::after': {
                                            borderColor: alpha('#1a1a1a', 0.08)
                                        }
                                    }}
                                />
                            )}
                        </React.Fragment>
                    ))}
                </List>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    p: drawerCollapsed ? 1 : 2,
                    borderTop: '1px solid rgba(0,0,0,0.08)',
                    position: 'relative',
                    zIndex: 2,
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Box display="flex" alignItems="center" justifyContent={drawerCollapsed ? 'center' : 'space-between'}>
                    <Tooltip title={drawerCollapsed ? "Expand Sidebar" : "Collapse Sidebar"} placement="right">
                        <IconButton
                            onClick={handleDrawerCollapse}
                            sx={{
                                color: '#1a1a1a',
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    color: '#ff5e17',
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {drawerCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </Tooltip>
                    {!drawerCollapsed && (
                        <Box display="flex" gap={1}>
                            <Tooltip title="Settings">
                                <IconButton
                                    sx={{
                                        color: alpha('#1a1a1a', 0.6),
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            color: '#ff5e17',
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Logout">
                                <IconButton
                                    sx={{
                                        color: alpha('#1a1a1a', 0.6),
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        '&:hover': {
                                            color: '#ff5e17',
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <LogoutIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', height: '100vh' }}>
            {/* Desktop Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    width: currentDrawerWidth,
                    flexShrink: 0,
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': {
                        width: currentDrawerWidth,
                        boxSizing: 'border-box',
                        border: 'none',
                        transition: theme.transitions.create('width', {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    },
                }}
            >
                <DrawerContent />
            </Drawer>

            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        border: 'none',
                    },
                }}
            >
                <DrawerContent />
            </Drawer>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 25%, #f8fafc 50%, #f1f5f9 75%, #e2e8f0 100%)',
                    position: 'relative',
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `radial-gradient(circle at 20% 80%, ${alpha('#ff5e17', 0.03)} 0%, transparent 50%),
                                   radial-gradient(circle at 80% 20%, ${alpha('#011855', 0.03)} 0%, transparent 50%),
                                   radial-gradient(circle at 40% 40%, ${alpha('#667eea', 0.02)} 0%, transparent 50%)`,
                        pointerEvents: 'none',
                        zIndex: 0
                    }
                }}
            >
                {/* Top App Bar */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
                        borderBottom: `1px solid ${alpha('#ff5e17', 0.1)}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 4px 20px ${alpha('#ff5e17', 0.08)}`,
                        position: 'relative',
                        zIndex: 1,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `linear-gradient(90deg, ${alpha('#ff5e17', 0.02)} 0%, transparent 20%, transparent 80%, ${alpha('#011855', 0.02)} 100%)`,
                            pointerEvents: 'none'
                        }
                    }}
                >
                    <Toolbar sx={{ justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
                        <Box display="flex" alignItems="center">
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{
                                    mr: 2,
                                    display: { md: 'none' },
                                    color: '#475569',
                                    '&:hover': {
                                        backgroundColor: alpha('#ff5e17', 0.1),
                                        color: '#ff5e17'
                                    }
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Box>
                                <Typography
                                    variant="h6"
                                    noWrap
                                    sx={{
                                        background: 'linear-gradient(135deg, #1e293b 0%, #475569 50%, #334155 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        fontWeight: 700,
                                        fontSize: '1.4rem',
                                        letterSpacing: '-0.025em'
                                    }}
                                >
                                    {(() => {
                                        const currentPage = navigationGroups
                                            .flatMap(group => group.items)
                                            .find(item => isActiveRoute(item.link));
                                        return currentPage ? currentPage.text : 'Admin Dashboard';
                                    })()}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: alpha('#64748b', 0.8),
                                        fontSize: '0.75rem',
                                        fontWeight: 500
                                    }}
                                >
                                    Contractor Test LA • Admin Portal
                                </Typography>
                            </Box>
                        </Box>

                        <Box display="flex" alignItems="center" gap={2}>
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: alpha('#fff', 0.7),
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${alpha('#e2e8f0', 0.8)}`,
                                    borderRadius: 2,
                                    p: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Tooltip title="Notifications">
                                    <IconButton
                                        size="small"
                                        sx={{
                                            color: '#64748b',
                                            '&:hover': {
                                                backgroundColor: alpha('#ff5e17', 0.1),
                                                color: '#ff5e17',
                                                transform: 'scale(1.05)'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <Badge
                                            badgeContent={4}
                                            sx={{
                                                '& .MuiBadge-badge': {
                                                    backgroundColor: '#ff5722',
                                                    color: 'white',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }
                                            }}
                                        >
                                            <NotificationsIcon fontSize="small" />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>

                                <Divider orientation="vertical" flexItem sx={{ borderColor: alpha('#e2e8f0', 0.6) }} />

                                <Chip
                                    avatar={
                                        <Avatar
                                            sx={{
                                                background: 'linear-gradient(135deg, #ff5e17 0%, #ff7849 100%)',
                                                width: 28,
                                                height: 28,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                border: '2px solid rgba(255,255,255,0.3)'
                                            }}
                                        >
                                            A
                                        </Avatar>
                                    }
                                    label="Admin"
                                    sx={{
                                        backgroundColor: alpha('#fff', 0.8),
                                        border: `1px solid ${alpha('#ff5e17', 0.2)}`,
                                        color: '#475569',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        '&:hover': {
                                            backgroundColor: alpha('#ff5e17', 0.05),
                                            borderColor: alpha('#ff5e17', 0.3),
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha('#ff5e17', 0.15)}`
                                        },
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                />
                            </Paper>
                        </Box>
                    </Toolbar>
                </AppBar>

                {/* Page Content */}
                <Box
                    sx={{
                        p: 3,
                        position: 'relative',
                        zIndex: 1,
                        minHeight: 'calc(100vh - 100px)'
                    }}
                >
                    {/* Content Background Enhancement */}
                    <Paper
                        elevation={0}
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.95) 100%)',
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${alpha('#e2e8f0', 0.6)}`,
                            borderRadius: 3,
                            p: 0,
                            overflow: 'hidden',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '4px',
                                background: 'linear-gradient(90deg, #ff5e17 0%, #667eea 50%, #011855 100%)',
                                opacity: 0.6
                            }
                        }}
                    >
                        <Box sx={{ p: 3 }}>
                            <Outlet />
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default AdminLayout;